import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  listMembers,
  onMembersChanged,
  sendInvitation,
  validateApiResponse,
} from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { mapApiMemberToUi, UI_STATUS_TO_API } from '../utils/mapApiMemberToUi';

const NOT_INVITED_PAGE_SIZE = 500;

/**
 * Banner above the Members filters for the members who have not been invited yet: `member` is the
 * first one and `count` how many there are. Independent of the list's filters and page. `send`
 * invites `member` (the banner goes away once the member refetch no longer finds them);
 * `fetchAll` resolves every not-invited member, for "Send all invitations" (which asks first);
 * `dismiss` hides the banner for this session (in memory, it comes back after a reload).
 *
 * @param {object} [options]
 * @param {(id: string) => Promise<object>} [options.sendInvite] Injectable (defaults to the mock service).
 * @param {(message: string) => void} [options.onError] Defaults to an error toast with a title.
 */
export const useNotInvitedBanner = ({ sendInvite = sendInvitation, onError } = {}) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [member, setMember] = useState(null);
  const [count, setCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const mountedRef = useRef(true);
  const sendingRef = useRef(false);
  const fetchIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      const res = await listMembers({
        page: 1,
        page_size: 1,
        filters: { status: UI_STATUS_TO_API[MEMBER_STATUS.NOT_INVITED] },
      });
      const { body } = validateApiResponse(res, t('members.list.loadError'));
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setMember(body?.items?.[0] ? mapApiMemberToUi(body.items[0]) : null);
      setCount(body?.items?.[0] ? (body.total ?? body.items.length) : 0);
    } catch {
      // The banner is a non-critical band: a failed fetch hides it instead of raising an error.
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        setMember(null);
        setCount(0);
      }
    }
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    const off = onMembersChanged(refetch);
    return () => {
      mountedRef.current = false;
      off();
    };
  }, [refetch]);

  const reportError = useCallback(
    (message) => {
      if (onError) {
        onError(message);
        return;
      }
      toast.error({ title: t('members.notInvited.errorTitle'), message, canClose: true });
    },
    [onError, t, toast],
  );

  const send = useCallback(async () => {
    if (!member || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await sendInvite(member.id);
      validateApiResponse(res, t('members.notInvited.errorMessage'));
      await refetch();
    } catch (err) {
      reportError(err?.message || t('members.notInvited.errorMessage'));
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setSending(false);
    }
  }, [member, refetch, reportError, sendInvite, t]);

  // Every not-invited member, page after page.
  const fetchAll = useCallback(async () => {
    const people = [];
    for (let page = 1; ; page += 1) {
      const res = await listMembers({
        page,
        page_size: NOT_INVITED_PAGE_SIZE,
        filters: { status: UI_STATUS_TO_API[MEMBER_STATUS.NOT_INVITED] },
      });
      const items = validateApiResponse(res, '').body.items.map(mapApiMemberToUi);
      people.push(...items);
      if (items.length < NOT_INVITED_PAGE_SIZE) return people;
    }
  }, []);

  const dismiss = useCallback(() => setDismissed(true), []);

  return useMemo(
    () => ({ member, count, dismissed, isVisible: Boolean(member) && !dismissed, sending, send, fetchAll, dismiss }),
    [member, count, dismissed, sending, send, fetchAll, dismiss],
  );
};
